import React, { type FunctionComponent, Fragment, useCallback } from 'react'
import { css } from '@emotion/react'
import { FormattedMessage } from 'react-intl'
import { Alert } from '@retailer-platform/shared-components'
import { BooleanParam, useQueryParam } from 'use-query-params'
import { Input } from 'gin-and-tonic/common/input/Input'
import { Button } from 'gin-and-tonic/common/button/Button'
import { LinkTextButton } from 'gin-and-tonic/routing/link-text-button/LinkTextButton'
import { DashRedirect } from 'legacy/dash-components/dash-redirect/DashRedirect'
import { usePublicAuthLogInForm } from 'domains/public/components/log-in/utils/publicLogInForm.hooks'
import { Text } from 'gin-and-tonic/common/text/Text'
import { PublicLayout } from 'domains/public/components/layout/PublicLayout'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import { spacing } from 'gin-and-tonic/foundation/spacing'
import { ReCaptchaCheckBox } from 'gin-and-tonic/containers/re-captcha-check-box/ReCaptchaCheckBox'
import { TextButton } from 'gin-and-tonic/common/text-button/TextButton'
import clientEnv from 'utils/global/clientEnv'

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  width: 240,
  justifyItems: 'flex-start',
})

const linkTextStyles = css({
  marginLeft: spacing.X0,
  marginRight: spacing.X0,
})

const backToEmailStyles = css({
  marginLeft: spacing.X8,
  marginRight: spacing.X0,
})

const alertMessageStyles = css({
  fontWeight: 600,
  marginBottom: spacing.X28,
})

export const PublicLogIn: FunctionComponent = () => {
  const {
    logInSuccess,
    disableFormFields,
    formErrorMessageId,
    emailFieldRef,
    passwordFieldRef,
    registerReCaptchaTokenField,
    setReCaptchaTokenValue,
    setReCaptchaResetHandler,
    onPasswordSubmit,
    onEmailSubmit,
    showPasswordAndCaptcha,
    resetToEmail,
    emailErrorMessageId,
    passwordErrorMessageId,
    reCaptchaTokenErrorMessageId,
    disableEmailForm,
    email,
  } = usePublicAuthLogInForm()

  const [expired, setExpired] = useQueryParam('sessionExpired', BooleanParam)

  const onEmailSubmitCallback = useCallback(
    (...args) => {
      // On login attempt, make the message go away.

      setExpired(undefined, 'replace')
      onEmailSubmit(...args)
    },
    [onEmailSubmit, setExpired]
  )

  if (logInSuccess) {
    return <DashRedirect push toRoute="app-halt-redirect" />
  }

  return (
    <PublicLayout headerMessageId="public.logIn.header" data-testid="login">
      {clientEnv.PUBLIC_CLIENT_ENVIRONMENT == 'enterprise-dev' && (
        <div css={alertMessageStyles}>
          <Alert variant={'info'}>
            <Text messageId="public.logIn.dev.message" size="medium" />
          </Alert>
        </div>
      )}

      {expired && (
        <div css={alertMessageStyles}>
          <Alert variant="warning" data-testid="session-expired-message">
            <Text messageId="public.logIn.sessionExpired" size="medium" />
          </Alert>
        </div>
      )}

      {showPasswordAndCaptcha ? (
        <form css={formStyles} onSubmit={onPasswordSubmit}>
          <Fragment>
            <Text>
              <FormattedMessage
                id="public.logIn.loggingInWithEmail"
                values={{
                  email,
                }}
              />
            </Text>
            <TextButton
              styles={backToEmailStyles}
              onClick={resetToEmail}
              messageId="public.logIn.backToEmail"
              data-testid="public-log-in-form-back-to-email"
            />
            <Input
              name="password"
              type="password"
              disabled={disableFormFields}
              errorMessageId={passwordErrorMessageId}
              placeholderMessageId="public.logIn.form.fields.password.placeholder"
              ref={passwordFieldRef}
              data-testid="public-log-in-form-field-password"
            />
            <Text messageId="public.logIn.forgotPasswordDetail">
              <LinkTextButton
                styles={linkTextStyles}
                toRoute="public-request-password-reset"
                messageId="public.logIn.forgotPasswordAction"
              />
            </Text>
            <ReCaptchaCheckBox
              setResetHandler={setReCaptchaResetHandler}
              data-testid="public-log-in-form-field-catpcha"
              action="public-log-in-form-re-captcha"
              onLoad={registerReCaptchaTokenField}
              onChange={setReCaptchaTokenValue}
              errorMessageId={reCaptchaTokenErrorMessageId}
            />
            <ErrorMessage
              collapse={false}
              messageId={formErrorMessageId}
              data-testid="public-log-in-form-error-message"
            />
            <Button
              disabled={disableFormFields}
              type="submit"
              messageId="public.logIn.form.submit"
              data-testid="public-log-in-form-submit"
            />
          </Fragment>
        </form>
      ) : (
        <form css={formStyles} onSubmit={onEmailSubmitCallback}>
          <Fragment>
            <Input
              name="email"
              disabled={disableEmailForm}
              errorMessageId={emailErrorMessageId}
              placeholderMessageId="public.logIn.form.fields.email.placeholder"
              ref={emailFieldRef}
              data-testid="public-log-in-form-field-email"
            />
            <ErrorMessage
              collapse={false}
              messageId={formErrorMessageId}
              data-testid="public-log-in-form-error-message"
            />
            <Button
              type="submit"
              disabled={disableEmailForm}
              data-testid="public-login-form-submit-email"
            >
              Continue
            </Button>
          </Fragment>
        </form>
      )}
    </PublicLayout>
  )
}
